import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/apiClient';
import { useAuthStore } from '../stores/useAuthStore';
import { clearAllDrafts } from '../lib/draftStorage';
import { useLanguageStore } from '../stores/useLanguageStore';
import i18n from '../i18n/config';
import LanguageSelector from '../components/UI/LanguageSelector';
import ThemeToggle from '../components/UI/ThemeToggle';

const TW_WORDS = [
  { text: 'Udbodhanam', font: 'font-display' },
  { text: 'ಉದ್ಬೋಧನಂ',   font: 'font-kn' },
  { text: 'उद्बोधन',     font: 'font-deva' },
  { text: 'उद्बोधनम्',   font: 'font-deva' },
] as const;

function splitGraphemes(text: string): string[] {
  if (typeof Intl !== 'undefined' && 'Segmenter' in Intl) {
    const seg = new Intl.Segmenter('und', { granularity: 'grapheme' });
    return Array.from(seg.segment(text), (s) => s.segment);
  }
  return [...text];
}

const ROLE_HOME: Record<string, string> = {
  ADMIN: '/admin',
  MENTOR: '/mentor',
  STUDENT: '/student',
};

interface ProfileData {
  id: string;
  username: string;
  fullName: string;
  role: string;
  languagePreference: string;
  consentStatus: string;
  mustChangePassword: boolean;
  accountType: string;
  paymentAccessReason: 'NEVER_PAID' | 'EXPIRED' | null;
}

export default function Login() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const location = useLocation();
  // Post-redirect flash banner. Senders pass a translation KEY (auth namespace),
  // not a pre-translated string, so language changes after navigation still render
  // correctly. Prefer router state (ResetPassword / Profile), fall back to
  // sessionStorage (ForcePasswordChange — stateless <Navigate> drops location.state).
  const [successMessageKey, setSuccessMessageKey] = useState<string>(
    () => (location.state as { messageKey?: string } | null)?.messageKey ?? '',
  );
  useEffect(() => {
    const flashKey = sessionStorage.getItem('authFlashKey');
    if (flashKey) {
      sessionStorage.removeItem('authFlashKey');
      setSuccessMessageKey((prev) => prev || flashKey);
    }
  }, []);
  // Cross-user draft-leak guard. Reaching /login means the session has ended
  // (explicit sign-out, session-expiry, or a fresh visit), so no authoring
  // "auto-recovery" draft should carry into the next user. Clearing here (rather
  // than only in logout()) is the robust catch: the logout paths redirect via
  // window.location.href, whose page-unload fires useDraftPersistence's flush and
  // re-writes the draft — this mount runs afterwards, on the fresh /login load.
  useEffect(() => {
    clearAllDrafts();
  }, []);
  const setAuth = useAuthStore((s) => s.setAuth);
  const setLanguage = useLanguageStore((s) => s.setLanguage);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [twIndex, setTwIndex] = useState(0);
  const [twDisplayed, setTwDisplayed] = useState('');
  const [twDeleting, setTwDeleting] = useState(false);

  useEffect(() => {
    const word = TW_WORDS[twIndex];
    const graphemes = splitGraphemes(word.text);
    const full = graphemes.join('');

    if (!twDeleting && twDisplayed === full) {
      const t = setTimeout(() => setTwDeleting(true), 1800);
      return () => clearTimeout(t);
    }
    if (twDeleting && twDisplayed === '') {
      setTwDeleting(false);
      setTwIndex((i) => (i + 1) % TW_WORDS.length);
      return;
    }
    const current = splitGraphemes(twDisplayed);
    const delay = twDeleting ? 45 : 110;
    const t = setTimeout(() => {
      setTwDisplayed(
        twDeleting
          ? current.slice(0, -1).join('')
          : graphemes.slice(0, current.length + 1).join(''),
      );
    }, delay);
    return () => clearTimeout(t);
  }, [twDisplayed, twDeleting, twIndex]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const loginRes = await apiClient.post<{ data: { isFirstLogin: boolean } }>(
        '/auth/login',
        { username, password, remember_me: rememberMe },
      );
      const isFirstLogin = loginRes.data.data.isFirstLogin ?? false;
      const profileRes = await apiClient.get<{ data: ProfileData }>('/users/me');
      const user = profileRes.data.data;
      setAuth(user, user.role, user.consentStatus, user.mustChangePassword ?? false, isFirstLogin);
      const apiLang = (user.languagePreference ?? 'EN').toLowerCase();
      const lang = sessionStorage.getItem('appLang') ?? apiLang;
      setLanguage(lang);
      sessionStorage.setItem('appLang', lang);
      await i18n.changeLanguage(lang);
      // If the user changed language on the login page before submitting,
      // LanguageSelector's PATCH was rejected (no JWT). Persist it now that
      // we have a valid session, so other browsers/devices pick it up.
      if (lang !== apiLang) {
        apiClient.patch('/users/me', { language_preference: lang.toUpperCase() }).catch(() => {});
      }
      if (user.mustChangePassword) {
        navigate('/set-password', { replace: true });
      } else {
        navigate(ROLE_HOME[user.role] ?? '/login', { replace: true });
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number } };
      const status = axiosErr.response?.status;
      if (status && status >= 500) {
        setError('login.errorServer');
      } else if (status === 403) {
        setError('login.errorDeactivated');
      } else {
        setError('login.errorInvalid');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="flex min-h-screen">
      {/* LEFT — orange hero panel */}
      <div className="hidden md:flex flex-1 bg-primary-500 text-white relative overflow-hidden flex-col justify-center px-16 py-16">
        {/* decorative concentric rings */}
        <div
          aria-hidden="true"
          className="absolute rounded-full"
          style={{ top: -140, right: -140, width: 460, height: 460, background: 'rgba(255,255,255,0.10)' }}
        />
        <div
          aria-hidden="true"
          className="absolute rounded-full"
          style={{ top: -50, right: -50, width: 300, height: 300, background: 'rgba(255,255,255,0.10)' }}
        />
        <div
          aria-hidden="true"
          className="absolute rounded-full"
          style={{ bottom: -180, left: -120, width: 380, height: 380, background: 'rgba(255,255,255,0.08)' }}
        />

        {/* Centered wordmark */}
        <div className="relative">
          <h1
            className={`${TW_WORDS[twIndex].font} text-[72px] leading-[84px] font-semibold text-white tracking-[-0.01em] min-h-[88px]`}
          >
            {twDisplayed}
            <span className="animate-tw-blink">|</span>
          </h1>
          <p className="font-display italic text-white/90 text-2xl mt-3 font-medium">
            &ldquo;Awakening through knowledge&rdquo;
          </p>
        </div>

        {/* Benefit bullets pinned to bottom */}
        <div className="absolute left-16 right-16 bottom-14 flex flex-col gap-3.5 text-white/95 text-sm">
          <div className="flex items-center gap-3">
            <BuildingIcon />
            <span>{t('login.benefit1', 'Built for SSC, UPSC, State PSC aspirants')}</span>
          </div>
          <div className="flex items-center gap-3">
            <BulbIcon />
            <span>{t('login.benefit2', 'Trap-analysis practice with detailed explanations')}</span>
          </div>
          <div className="flex items-center gap-3">
            <CompassIcon />
            <span>{t('login.benefit3', 'Career guidance for Indian government jobs')}</span>
          </div>
        </div>
      </div>

      {/* RIGHT — form panel */}
      <div className="w-full md:flex-1 bg-surface flex flex-col relative">

        {/* Controls — always top-right */}
        <div className="absolute top-5 right-5 md:top-7 md:right-8 flex items-center gap-2 z-10">
          <ThemeToggle />
          <LanguageSelector />
        </div>

        {/* Mobile brand — white bg, centered, minimal */}
        <div className="md:hidden flex flex-col items-center pt-24 pb-6 px-6">
          <div className={`${TW_WORDS[twIndex].font} text-[34px] leading-tight font-semibold text-primary-600 tracking-[-0.01em] min-h-[44px] text-center`}>
            {twDisplayed}
            <span className="animate-tw-blink text-primary-400">|</span>
          </div>
          <p className="font-display italic text-text-muted text-[15px] font-medium mt-1.5 text-center">
            &ldquo;Awakening through knowledge&rdquo;
          </p>
        </div>

        {/* Form */}
        <div className="flex-1 flex flex-col md:justify-center px-6 pb-10 pt-4 md:px-[72px] md:py-[60px]">
          <div className="max-w-[440px] w-full mx-auto">
            <h2 className="text-[36px] leading-[44px] font-bold text-earth-800 tracking-[-0.015em]">
              {t('login.welcome', 'Welcome back')}
            </h2>
            <p className="text-earth-600 mt-1.5 text-sm">
              {t('login.subtitle', 'Sign in to continue your learning journey')}
            </p>

            {successMessageKey && (
              <p className="mt-4 text-sm text-growth-700 bg-growth-50 border border-growth-100 rounded-lg px-3 py-2">
                {t(successMessageKey)}
              </p>
            )}

            <form onSubmit={handleSubmit} className="mt-8 space-y-5">
              <div>
                <label className="block text-[13px] font-semibold text-earth-700 mb-1.5">
                  {t('login.username')}<span className="ml-0.5">*</span>
                </label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full h-[42px] border border-border rounded-lg px-[14px] text-[14px] text-text bg-surface focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100 placeholder:text-earth-400"
                  required
                  autoComplete="username"
                />
              </div>
              <div>
                <label className="block text-[13px] font-semibold text-earth-700 mb-1.5">
                  {t('login.password')}<span className="ml-0.5">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full h-[42px] border border-border rounded-lg px-[14px] pr-11 text-[14px] text-text bg-surface focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100 placeholder:text-earth-400"
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-earth-400 hover:text-earth-600"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="w-4 h-4 rounded border-border accent-primary-500 cursor-pointer"
                  />
                  <span className="text-sm text-earth-600">{t('login.rememberMe')}</span>
                </label>
                <Link to="/forgot-password" className="text-sm text-primary-600 hover:underline">
                  {t('login.forgotPassword')}
                </Link>
              </div>
              {error && <p className="text-sm text-error">{t(error)}</p>}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-primary-500 text-white font-semibold rounded-lg py-2.5 text-sm hover:bg-primary-600 active:bg-primary-700 disabled:opacity-50"
              >
                {loading ? t('login.signingIn') : t('login.submit')}
              </button>
            </form>

            <p className="text-center mt-5 text-sm text-earth-600">
              {t('login.noAccount')}{' '}
              <Link to="/aspirant/signup" className="text-primary-600 hover:underline font-medium">
                {t('login.signupLink')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </main>
  );
}

function EyeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
      <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
      <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
      <line x1="2" y1="2" x2="22" y2="22" />
    </svg>
  );
}

function BuildingIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="shrink-0">
      <rect x="1" y="5" width="14" height="10" rx="1" stroke="currentColor" strokeWidth="1.5" />
      <path d="M5 15V9h6v6" stroke="currentColor" strokeWidth="1.5" />
      <path d="M5 5l1-4h4l1 4" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <rect x="6.5" y="10" width="3" height="5" rx="0.5" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  );
}

function BulbIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="shrink-0">
      <path d="M8 1.5a4.5 4.5 0 0 1 3 7.9l-.5.6v1H5.5V10l-.5-.6A4.5 4.5 0 0 1 8 1.5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M6 13.5h4M6.5 15h3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function CompassIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="shrink-0">
      <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M10.5 5.5 9 9l-3.5 1.5L7 7l3.5-1.5z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  );
}
